import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { API } from '../services/apiConfig';
import CustomDialog from './CustomDialog';
import axios from 'axios';
import { useOtpTimer } from './useOtpTimer';
import './Auth.css';

// step: 'login' | 'fp-email' | 'fp-otp' | 'fp-reset'
function Login() {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [dialog, setDialog] = useState(null);
  const [step, setStep] = useState('login');
  const [fpEmail, setFpEmail] = useState('');
  const [fpOtp, setFpOtp] = useState('');
  const [fpNew, setFpNew] = useState('');
  const [fpConfirm, setFpConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const { countdown, canResend, restart } = useOtpTimer(30);
  const navigate = useNavigate();

  const showDialog = (message, type) => setDialog({ message, type, onClose: () => setDialog(null) });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await authAPI.login(formData.email, formData.password);
      if (response.token) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response));
        window.location.href = response.role === 'ADMIN' ? '/admin' : '/';
      } else {
        showDialog(response.message || 'Invalid credentials', 'error');
      }
    } catch (error) {
      showDialog(error.message || 'Something went wrong', 'error');
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: fpEmail });
      setStep('fp-otp');
      restart();
    } catch (err) {
      showDialog(err.response?.data || 'Email not found', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    setResendMsg('');
    try {
      await authAPI.resendOtp(fpEmail);
      setResendMsg('OTP resent successfully');
      restart();
    } catch (err) {
      setResendMsg(err.message || 'Failed to resend OTP');
    } finally {
      setResending(false);
    }
  };

  const handleVerifyOtp = (e) => {
    e.preventDefault();
    if (fpOtp.trim().length < 4) { showDialog('Enter the OTP sent to your email', 'error'); return; }
    setStep('fp-reset');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (fpNew !== fpConfirm) { showDialog('Passwords do not match', 'error'); return; }
    if (fpNew.length < 6) { showDialog('Password must be at least 6 characters', 'error'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { email: fpEmail, otp: fpOtp, newPassword: fpNew });
      showDialog('Password reset successfully! Please login.', 'success');
      setStep('login');
      setFpEmail(''); setFpOtp(''); setFpNew(''); setFpConfirm('');
    } catch (err) {
      showDialog(err.response?.data || 'Invalid OTP or request expired', 'error');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setStep('login'); setFpEmail(''); setFpOtp(''); setFpNew(''); setFpConfirm(''); };

  return (
    <>
      <div className="auth-container" onClick={step === 'login' ? () => navigate('/') : undefined} style={{ alignItems: 'flex-start', overflowY: 'auto' }}>
        {step === 'login' && (
          <div className="auth-card" onClick={(e) => e.stopPropagation()}>
            <button className="auth-back-btn" onClick={() => navigate('/')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 5l-7 7 7 7"/>
              </svg>
              Back to Home
            </button>
            <div className="auth-header">
              <img src="/images/corgif-logo.png" alt="CorGif Logo" className="auth-logo" />
              <h2>Welcome Back</h2>
              <p>Login to your account</p>
            </div>
            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label>Email</label>
                <input type="email" placeholder="Enter your work email" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input type="password" placeholder="Enter your password" value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })} required />
              </div>
              <div style={{ textAlign: 'right', marginTop: '-12px', marginBottom: '16px' }}>
                <span onClick={() => setStep('fp-email')} className="fp-link">Forgot Password?</span>
              </div>
              <button type="submit" className="auth-submit-btn">Login</button>
            </form>
            <div className="auth-footer">
              <p>Don't have an account? <a href="/signup">Sign Up</a></p>
            </div>
          </div>
        )}

        {step === 'fp-email' && (
          <div className="otp-card" onClick={(e) => e.stopPropagation()}>
            <svg width="56" height="56" viewBox="0 0 56 56" className="otp-check-icon">
              <circle cx="28" cy="28" r="28" fill="#107FDF" />
              <text x="28" y="35" textAnchor="middle" fontSize="24" fill="white">✉</text>
            </svg>
            <h3 style={{ margin: 0, color: '#222' }}>Forgot Password?</h3>
            <p className="otp-sent-text">Enter your registered email and we'll send you an OTP.</p>
            <form className="otp-form" onSubmit={handleSendOtp}>
              <input className="otp-input" style={{ letterSpacing: 'normal' }} type="email"
                placeholder="Enter your email" value={fpEmail}
                onChange={(e) => setFpEmail(e.target.value)} required />
              <button className="otp-submit-btn" type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Send OTP'}
              </button>
            </form>
            <span onClick={reset} className="fp-link otp-back">← Back to Login</span>
          </div>
        )}

        {step === 'fp-otp' && (
          <div className="otp-card" onClick={(e) => e.stopPropagation()}>
            <svg width="56" height="56" viewBox="0 0 56 56" className="otp-check-icon">
              <circle cx="28" cy="28" r="28" fill="#107FDF" />
              <polyline points="16,28 24,36 40,20" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <h3 style={{ margin: 0, color: '#222' }}>Enter OTP</h3>
            <p className="otp-sent-text">OTP sent to <strong>{fpEmail}</strong></p>
            <form className="otp-form" onSubmit={handleVerifyOtp}>
              <input className="otp-input" type="text" placeholder="Enter OTP" maxLength={6}
                value={fpOtp} onChange={(e) => setFpOtp(e.target.value)} required />
              <button className="otp-submit-btn" type="submit">Verify OTP</button>
              <div style={{ textAlign: 'center', marginTop: '4px' }}>
                {canResend ? (
                  <button type="button" onClick={handleResendOtp} disabled={resending}
                    style={{ background: 'none', border: 'none', color: '#107FDF', fontWeight: '600', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}>
                    {resending ? 'Resending...' : 'Resend OTP'}
                  </button>
                ) : (
                  <span style={{ fontSize: '13px', color: '#888' }}>Resend OTP in {countdown}s</span>
                )}
                {resendMsg && (
                  <p style={{ fontSize: '12px', marginTop: '4px', color: resendMsg.includes('success') ? '#16a34a' : '#dc2626' }}>{resendMsg}</p>
                )}
              </div>
            </form>
            <span onClick={() => setStep('fp-email')} className="fp-link otp-back">← Change Email</span>
          </div>
        )}

        {step === 'fp-reset' && (
          <div className="otp-card" onClick={(e) => e.stopPropagation()}>
            <svg width="56" height="56" viewBox="0 0 56 56" className="otp-check-icon">
              <circle cx="28" cy="28" r="28" fill="#9A1111" />
              <text x="28" y="36" textAnchor="middle" fontSize="22" fill="white">🔒</text>
            </svg>
            <h3 style={{ margin: 0, color: '#222' }}>Set New Password</h3>
            <form className="otp-form" onSubmit={handleResetPassword}>
              <input className="otp-input" style={{ letterSpacing: 'normal' }} type="password"
                placeholder="New password" value={fpNew}
                onChange={(e) => setFpNew(e.target.value)} required minLength={6} />
              <input className="otp-input" style={{ letterSpacing: 'normal' }} type="password"
                placeholder="Confirm new password" value={fpConfirm}
                onChange={(e) => setFpConfirm(e.target.value)} required minLength={6} />
              <button className="otp-submit-btn" type="submit" disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
            <span onClick={() => setStep('fp-otp')} className="fp-link otp-back">← Back</span>
          </div>
        )}
      </div>
      {dialog && <CustomDialog message={dialog.message} type={dialog.type} onClose={dialog.onClose} />}
    </>
  );
}

export default Login;
